import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CommitmentTargetPreviewSchema,
  CommitmentTargetRowSchema,
} from 'src/Ghg/interfaces';
import {
  formatStatementDate,
  formatStatementDecimal,
  resolveStatementLanguage,
  StatementLanguage,
} from './statementFormat';

// RG 28.08 9.1 (D5): the full text of the Target Statement ("Oświadczenie o
// wyznaczeniu celów redukcji emisji") on screen, in the language of the
// DOCUMENT the carrier signs -- not of the UI. cs/nl have no Statement of
// their own and sign the English PDF, so they read the English text here too.
// The wording lives in `ghg.supplierCommitment.target.statement.*` and has to
// stay identical to the text layer of public/assets/raben/target-statement-
// {pl,en,de}.pdf (targetStatementProse.test.ts pins that).
//
// Not a single figure is computed here (BR-1): every value comes from the
// server's preview/stored row and is only formatted. Missing values show the
// same blanks as the paper form, and `yearSlot` lets the caller put the base
// year Select exactly where the form has its first dotted blank.

const KEY_PREFIX = 'ghg.supplierCommitment.target.statement';

// Shown where a value is not known yet (the paper form has dotted blanks).
export const STATEMENT_BLANK = '……';

type StatementValues =
  CommitmentTargetPreviewSchema | CommitmentTargetRowSchema;

type Placeholder =
  | 'baseYear'
  | 'targetYear'
  | 'pct'
  | 'baseEmissions'
  | 'targetEmissions'
  | 'companyName'
  | 'taxId'
  | 'date';

type Slots = Partial<Record<Placeholder, React.ReactNode>>;

// One capture group, so `split` alternates text and placeholder names.
const PLACEHOLDER_SPLIT = /\{\{\s*(\w+)\s*\}\}/;

// A filled-in value stands out from the surrounding prose.
function Value({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        fontWeight: 700,
        backgroundColor: 'rgba(71, 75, 77, 0.12)',
        borderRadius: '4px',
        px: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

function Blank() {
  return <span>{STATEMENT_BLANK}</span>;
}

// The date on the document: the preview's day, or -- for a stored target --
// the moment it was accepted.
function statementDateOf(values: StatementValues): string {
  if ('statement_date' in values) return values.statement_date;
  return values.accepted_at ?? values.created_at;
}

function buildSlots(
  values: StatementValues | undefined,
  lang: StatementLanguage
): Slots {
  if (!values) return {};
  return {
    baseYear: <Value>{values.base_year}</Value>,
    targetYear: <Value>{values.target_year}</Value>,
    pct: <Value>{formatStatementDecimal(values.reduction_pct, lang)}</Value>,
    baseEmissions: (
      <Value>{formatStatementDecimal(values.base_emissions, lang)}</Value>
    ),
    targetEmissions: (
      <Value>{formatStatementDecimal(values.target_emissions, lang)}</Value>
    ),
    companyName: <Value>{values.company_name}</Value>,
    taxId: <Value>{values.vat_prefixed}</Value>,
    date: <Value>{formatStatementDate(lang, statementDateOf(values))}</Value>,
  };
}

// Splits a locale string on its `{{placeholders}}` and puts a node (the value,
// the caller's slot, or a blank) in place of each. The text is kept as plain
// prose in the locale files -- no markup -- so it can be compared with the PDF.
function fillTemplate(template: string, slots: Slots): React.ReactNode[] {
  return template.split(PLACEHOLDER_SPLIT).map((part, i) => {
    if (i % 2 === 0) return part;
    const node = slots[part as Placeholder];
    return (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={i}>
        {node === undefined ? <Blank /> : node}
      </React.Fragment>
    );
  });
}

function useStatementTranslation() {
  const { i18n } = useTranslation();
  const lang = resolveStatementLanguage(i18n.language);
  const fixedT = i18n.getFixedT(lang, undefined, KEY_PREFIX);
  // `skipInterpolation` hands back the raw string, `{{placeholders}}` intact.
  const template = (key: string): string =>
    String(fixedT(key, { skipInterpolation: true }));
  return { lang, template };
}

const sectionHeadingSx = { fontWeight: 600, mt: 2, mb: 1 };
const listSx = { m: 0, mb: 1, pl: 3 };

interface Props {
  // Server values: the preview (before accepting) or the stored target.
  // Absent -> every value is a blank.
  values?: CommitmentTargetPreviewSchema | CommitmentTargetRowSchema;
  // Rendered in place of the FIRST base year (point 1 of "Defining the base
  // year and time horizon") -- where the form has its dropdown of years.
  yearSlot?: React.ReactNode;
}

export default function TargetStatementText({ values, yearSlot }: Props) {
  const { lang, template } = useStatementTranslation();
  const slots = buildSlots(values, lang);
  const fill = (key: string, extra: Slots = {}) =>
    fillTemplate(template(key), { ...slots, ...extra });

  const yearSlots: Slots = yearSlot === undefined ? {} : { baseYear: yearSlot };

  return (
    <Box data-testid="target-statement" lang={lang}>
      <Typography variant="h6" component="h6" sx={{ mb: 2 }}>
        {fill('heading')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {fill('intro1')}
      </Typography>
      <Typography variant="body2">{fill('intro2')}</Typography>

      <Typography variant="body2" component="p" sx={sectionHeadingSx}>
        {fill('scopeHeading')}
      </Typography>
      <Box component="ol" sx={listSx}>
        <Typography variant="body2" component="li">
          {fill('scopeItem1')}
        </Typography>
        <Typography variant="body2" component="li">
          {fill('scopeItem2')}
        </Typography>
        <Typography variant="body2" component="li">
          {fill('scopeItem3')}
        </Typography>
      </Box>

      <Typography variant="body2" component="p" sx={sectionHeadingSx}>
        {fill('baseYearHeading')}
      </Typography>
      <Box component="ol" sx={listSx}>
        <Typography variant="body2" component="li">
          {fill('baseYearSentence', yearSlots)}
        </Typography>
        <Typography variant="body2" component="li">
          {fill('timeHorizonSentence')}
        </Typography>
      </Box>

      <Typography variant="body2" component="p" sx={sectionHeadingSx}>
        {fill('reductionHeading')}
      </Typography>
      <Box component="ol" sx={listSx}>
        <Typography variant="body2" component="li">
          {fill('reductionSentence')}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {fill('baseEmissionsLine')}
      </Typography>
      <Typography variant="body2">{fill('targetEmissionsLine')}</Typography>

      <Typography variant="body2" sx={{ mt: 2 }}>
        {fill('companyNameLine')}
      </Typography>
      <Typography variant="body2">{fill('taxIdLine')}</Typography>
      <Typography variant="body2">{fill('dateLine')}</Typography>
      <Typography variant="body2">{fill('signatureLine')}</Typography>
    </Box>
  );
}

// RG 28.08 10.2: the Statement's SBTi sentence ("Przewoźnik zobowiązuje się
// do osiągnięcia redukcji emisji ... o 51,4% do roku 2035 ..."), on its own,
// for the "target set" view. Same wording, language and number format as in
// the Statement above.
export function StatementReductionSentence({
  values,
}: {
  values: CommitmentTargetPreviewSchema | CommitmentTargetRowSchema;
}) {
  const { lang, template } = useStatementTranslation();
  return (
    <Typography variant="body1" lang={lang} sx={{ mb: 2 }}>
      {fillTemplate(template('reductionSentence'), buildSlots(values, lang))}
    </Typography>
  );
}
